/* eslint-disable react/button-has-type */
import React from 'react';
import { FOOTER_ADDRESSES, FooterLinksItem, Margin, Footer as UpgradeFooter } from '@upgrade/react-components';
import getCopyrightYear from '@upgrade/react-components/Footer/copyright-year';
import { joinPaths } from '@upgrade/ui-utils/utils/url';
import { Trans } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';

import { Link } from 'gatsby';
import { Text } from './common/TypographyPublicSite';

import {
  AB_PATH,
  ACCESSIBILITY_PATH,
  CAREERS_PATH,
  CREDIT_HEALTH_INSIGHTS_PATH,
  FLEX_PAY,
  FLEX_PAY_FAQ,
  FLEX_PAY_FIND_PARTNERS,
  FLEX_PAY_LICENSES,
  FLEX_PAY_PARTNERS,
  FLEX_PAY_PRIVACY,
  FLEX_PAY_TERMS,
  PRESS,
  REVIEWS_PATH,
  UPLIFT_DEVELOPER_PORTAL
} from '../constants/path-constants';
import { useLocale } from '../utils/locale';
import LocaleFooterMenu from './common/LocaleSwitcher/LocaleFooterMenu';
import { FooterContainer, FooterLinkGroup } from './styles';

const CanadaFooter = () => {
  const { locale, isCanadaEnglish, isCanadaFrench } = useLocale();
  const { t } = useLingui();
  const { pathname: localePath } = locale || {};

  const legal = (
    <div>
      <Trans
        id="footer.legalDisclaimer"
        message="Flex Pay is a service provided by Uplift Canada Services ULC, a subsidiary of Upgrade, Inc. Flex Pay is not available in Quebec. Flex Pay is not available for all products or services. Flex Pay is not available for all merchants. Flex Pay is not available for all customers. Flex Pay is not available for all transactions. Flex Pay is not available for all payment methods. Flex Pay is not available for all currencies.<0></0> <1></1> Address: 410 Adelaide Street West, Suite 220, Toronto, Ontario M5V1S8"
        components={{
          0: (
            <a href={`https://upgrade.com${localePath}/flex-pay/privacy/`} target="_blank" rel="noopener noreferrer">
              Upgrade.com{localePath}/flex-pay/privacy.{' '}
            </a>
          ),
          1: (
            <a href={`https://upgrade.com${localePath}/flex-pay/terms/`} target="_blank" rel="noopener noreferrer">
              Upgrade.com{localePath}/flex-pay/terms.{' '}
            </a>
          )
        }}
      />
    </div>
  );

  const links = [
    <FooterLinkGroup key="products-li">
      <Text bold className="footer-menu__category">
        <Trans id="footer.quickLinks" message="Quick Links" />
      </Text>
      <ul className="list--unstyled">
        <FooterLinksItem {...{ Link, linkProps: { to: joinPaths(localePath, FLEX_PAY) } }}>
          <Trans id="footer.howFlexPayWorks" message="How Flex Pay Works" />
        </FooterLinksItem>
        <Margin right={{ sm: isCanadaEnglish ? '3xl' : 'none' }}>
          <FooterLinksItem {...{ Link, linkProps: { to: joinPaths(localePath, FLEX_PAY_FIND_PARTNERS) } }}>
            <Trans id="footer.findFlexPayPartners" message="Find Flex Pay Partners" />
          </FooterLinksItem>
        </Margin>
        <FooterLinksItem {...{ Link, linkProps: { to: joinPaths(localePath, FLEX_PAY_PARTNERS) } }}>
          <Trans id="footer.becomeAPartner" message="Become a Partner" />
        </FooterLinksItem>
      </ul>
    </FooterLinkGroup>,
    <FooterLinkGroup key="products-li">
      <Text bold className="footer-menu__category" uppercase>
        <Trans id="footer.company" message="Company" />
      </Text>
      <ul className="list--unstyled">
        <FooterLinksItem {...{ Link, linkProps: { to: joinPaths(localePath, AB_PATH) } }}>
          <Trans id="footer.aboutUs" message="About Us" />
        </FooterLinksItem>
        <FooterLinksItem {...{ Link, linkProps: { to: CAREERS_PATH } }}>
          <Trans id="footer.careers" message="Careers" />
        </FooterLinksItem>
        {isCanadaEnglish ? (
          <>
            <FooterLinksItem {...{ Link, linkProps: { to: PRESS } }}>
              <Trans id="footer.pressRoom" message="Press Room" />
            </FooterLinksItem>
            <FooterLinksItem {...{ Link, linkProps: { to: REVIEWS_PATH } }}>
              <Trans id="footer.reviews" message="Reviews" />
            </FooterLinksItem>
          </>
        ) : null}
      </ul>
    </FooterLinkGroup>,
    <FooterLinkGroup key="region-li" className="hidden visible-md">
      <LocaleFooterMenu />
    </FooterLinkGroup>,
    <FooterLinkGroup key="products-li">
      <Text bold className="footer-menu__category">
        <Trans id="footer.legal" message="Legal" />
      </Text>
      <ul className="list--unstyled">
        <FooterLinksItem {...{ Link, linkProps: { to: joinPaths(localePath, FLEX_PAY_PRIVACY) } }}>
          <Trans id="footer.privacy" message="Privacy" />
        </FooterLinksItem>
        <FooterLinksItem {...{ Link, linkProps: { to: joinPaths(localePath, FLEX_PAY_TERMS) } }}>
          <Trans id="footer.termsOfUse" message="Terms of Use" />
        </FooterLinksItem>
        <FooterLinksItem {...{ Link, linkProps: { to: joinPaths(localePath, FLEX_PAY_TERMS) } }}>
          <Margin right={{ sm: isCanadaEnglish ? '3xl' : 'xl' }}>
            <Trans id="footer.agentsTermsOfUse" message="Agents Terms of Use" components={{ 0: <br /> }} />
          </Margin>
        </FooterLinksItem>
        <FooterLinksItem {...{ Link, linkProps: { to: joinPaths(localePath, FLEX_PAY_LICENSES) } }}>
          <Trans id="footer.licenses" message="Licenses" />
        </FooterLinksItem>
      </ul>
    </FooterLinkGroup>,
    <FooterLinkGroup key="products-li">
      <Text bold className="footer-menu__category">
        <Trans id="footer.resources" message="Resources" />
      </Text>
      <ul className="list--unstyled">
        {isCanadaEnglish ? (
          <>
            <FooterLinksItem {...{ Link, linkProps: { to: CREDIT_HEALTH_INSIGHTS_PATH } }}>
              Credit Health Insights
            </FooterLinksItem>
            <FooterLinksItem
              target="_blank"
              href="https://upgrade.zendesk.com/hc/en-us"
              linkProps={{ rel: 'noopener' }}
            >
              Help Center
            </FooterLinksItem>
          </>
        ) : null}
        {isCanadaFrench ? (
          <FooterLinksItem {...{ Link, linkProps: { to: joinPaths(localePath, FLEX_PAY_FAQ) } }}>
            <Trans id="footer.faq" message="FAQ" />
          </FooterLinksItem>
        ) : null}

        <FooterLinksItem {...{ Link, linkProps: { to: joinPaths(localePath, ACCESSIBILITY_PATH) } }}>
          <Trans id="footer.accessibility" message="Accessibility" />
        </FooterLinksItem>
        <FooterLinksItem {...{ Link, href: UPLIFT_DEVELOPER_PORTAL }}>
          <Trans id="footer.developerPortal" message="Developer Portal" />
        </FooterLinksItem>
      </ul>
    </FooterLinkGroup>,
    <FooterLinkGroup key="region-li" className="hidden-md">
      <LocaleFooterMenu />
    </FooterLinkGroup>
  ];

  const contact = () => {
    const handleCopy = (e, email) => {
      e.preventDefault();
      e.clipboardData.setData('text/plain', email);
    };

    return (
      <Text as="p" size="xs" weight={300}>
        {isCanadaFrench ? (
          <>
            <Trans id="footer.paymentAddress" components={{ 0: <br /> }} />
            <br />
            <br />
          </>
        ) : null}
        <Trans id="footer.contactUs" message="For inquiries, contact us at <0></0>" components={{ 0: <br /> }} />
        <Text
          className="footer-contact"
          onCopy={e =>
            handleCopy(
              e,
              t({
                id: 'footer.email',
                message: 'customercare@upgrade.com'
              })
            )
          }
        >
          moc.edargpu<Text className="hidden">null</Text>
          <Trans id="footer.emailBackwards" message="@eracremotsuc" />
          <Text className="hidden">null</Text>
        </Text>
      </Text>
    );
  };

  return (
    <FooterContainer>
      <UpgradeFooter
        {...{
          links,
          contact: contact(),
          legal,
          address: FOOTER_ADDRESSES.TORONTO,
          company: 'Uplift Canada Services ULC',
          showPaymentAddress: !isCanadaFrench,
          allRightsReservedMessage: <Trans id="footer.allRightsReserved" message="All rights reserved." />,
          copyrightMessage: (
            <Trans
              id="footer.copyrightMessage"
              message="Copyright <0></0> Upgrade, Inc."
              components={{ 0: <>{getCopyrightYear()}</> }}
            />
          )
        }}
      />
    </FooterContainer>
  );
};

export default CanadaFooter;
